import { TooltipTrigger } from '@toggle/design-system';
import React, {
  forwardRef,
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useTerminalNavigate } from '~/hooks/use-terminal-navigate/useTerminalNavigate';
import { appPaths } from '~/routes/app-paths';

import { TooltipContainer } from '../tooltip-container/TooltipContainer';
import * as S from './ChatInput.styles';

export interface ChatInputProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  children?: ReactNode;
  displayReset?: boolean;
  resetChat?: () => void;
  updateRows: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isMessagePending: boolean;
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      children,
      onChange,
      disabled = false,
      displayReset = true,
      resetChat,
      value,
      rows,
      updateRows,
      isMessagePending,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation('chat');
    const navigateTerminal = useTerminalNavigate();
    const location = useLocation();

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateRows(event);
      onChange?.(event);
    };

    const onClickNewChat = () => {
      if (resetChat) {
        resetChat();

        if (location.pathname !== appPaths.alfred || location.search) {
          navigateTerminal({
            to: appPaths.alfred,
          });
        }
      }
    };

    return (
      <S.ChatInputWrapper $isMessagePending={isMessagePending}>
        {displayReset && (
          <TooltipContainer
            label={t('tooltip.chatReset')}
            placement="top-start"
            trigger={[TooltipTrigger.Hover]}
          >
            <S.ChatIconWrapper
              data-testid="reset-chat"
              onClick={onClickNewChat}
            >
              <S.ChatPromptIcon iconName="ChatNew" />
            </S.ChatIconWrapper>
          </TooltipContainer>
        )}
        <S.ChatInput>
          <S.TextareaElement
            id="dropdown-trigger"
            data-testid="chat-input"
            as="textarea"
            ref={ref}
            onChange={handleChange}
            rows={rows}
            disabled={disabled}
            value={value}
            {...rest}
          />
          {children}
        </S.ChatInput>
      </S.ChatInputWrapper>
    );
  }
) as ForwardRefExoticComponent<
  ChatInputProps & RefAttributes<HTMLTextAreaElement>
> & {
  Styled: typeof S;
};

ChatInput.Styled = S;
